const spAllowsMotion=()=>!matchMedia('(prefers-reduced-motion: reduce)').matches;
const spAnimate=(element,frames,duration=220)=>{
  if(element?.animate&&spAllowsMotion())element.animate(frames,{duration,easing:'cubic-bezier(.2,.65,.3,1)'});
};
/* Native MP4 markup is the fallback. This controller adds responsive sources and playback controls. */
class SPCampaign extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.video = this.querySelector('video');
    this.button = this.querySelector('.sp-film-toggle');
    if (!this.video || !this.button) return;
    this.controller = new AbortController();
    this.cleanups = [];
    this.mobile = matchMedia('(max-width: 749px)');
    this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
    this.manualPause = false;
    const opts = {signal:this.controller.signal};
    const listenMedia = (query, fn) => {
      if (query.addEventListener) { query.addEventListener('change',fn); this.cleanups.push(()=>query.removeEventListener('change',fn)); }
      else { query.addListener(fn); this.cleanups.push(()=>query.removeListener(fn)); }
    };
    this.video.muted = true;
    this.video.defaultMuted = true;
    this.video.setAttribute('muted','');
    this.video.setAttribute('playsinline','');
    this.video.setAttribute('webkit-playsinline','');
    this.applySource();
    this.video.addEventListener('playing',()=>{this.classList.add('is-playing');this.updateLabel(true);},opts);
    this.video.addEventListener('pause',()=>this.updateLabel(false),opts);
    this.video.addEventListener('error',()=>{this.classList.remove('is-playing');this.updateLabel(false);},opts);
    this.button.addEventListener('click',()=>{
      if (this.video.paused) {this.manualPause=false;this.play();}
      else {this.manualPause=true;this.video.pause();}
    },opts);
    listenMedia(this.mobile,()=>{this.applySource();if(this.shouldAutoplay())this.play();});
    listenMedia(this.reduced,()=>{if(this.reduced.matches)this.video.pause();else if(this.shouldAutoplay())this.play();});
    document.addEventListener('visibilitychange',()=>{
      if(document.hidden)this.video.pause();else if(this.inView&&this.shouldAutoplay())this.play();
    },opts);
    this.inView = true;
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(entries=>{
        this.inView=entries[0].isIntersecting;
        if(!this.inView)this.video.pause();else if(this.shouldAutoplay())this.play();
      },{threshold:.08});
      this.observer.observe(this);
    }
    if(this.reduced.matches || !this.hasAttribute('data-autoplay')) {
      this.video.autoplay=false;this.video.pause();this.updateLabel(false);
    } else {this.video.autoplay=true;this.play();}
  }
  shouldAutoplay(){return this.hasAttribute('data-autoplay')&&!this.reduced.matches&&!this.manualPause&&!document.hidden;}
  applySource(){
    const url=this.mobile.matches?this.video.dataset.mobileSrc:this.video.dataset.desktopSrc;
    if(!url)return;
    const current=this.video.getAttribute('src')||this.video.querySelector('source')?.getAttribute('src');
    if(current!==url){this.video.src=url;this.video.load();this.classList.remove('is-playing');}
  }
  play(){
    const result=this.video.play();
    if(result?.catch)result.catch(()=>this.updateLabel(false));
  }
  updateLabel(playing){
    this.button.textContent=playing?(this.button.dataset.pause||'PAUSAR'):(this.button.dataset.play||'REPRODUCIR');
    this.button.setAttribute('aria-label',this.button.textContent+' VIDEO');
  }
  disconnectedCallback(){this.observer?.disconnect();this.controller?.abort();this.cleanups?.forEach(fn=>fn());this.video?.pause();this.initialized=false;}
}
class SPProductFlip extends HTMLElement {
  connectedCallback(){
    if(this.controller)return;
    const button=this.querySelector('button');if(!button)return;
    this.controller=new AbortController();button.hidden=false;
    button.addEventListener('click',()=>{
      const back=this.classList.toggle('is-back');
      button.setAttribute('aria-pressed',String(back));
      button.textContent=back?(button.dataset.front||'VOLVER'):(button.dataset.back||'OTRA VISTA');
      button.setAttribute('aria-label',button.textContent+' · '+this.querySelector('h3').textContent);
    },{signal:this.controller.signal});
  }
  disconnectedCallback(){this.controller?.abort();this.controller=null;}
}
class SPFitStudio extends HTMLElement {
  connectedCallback(){
    if(this.controller)return;
    this.controller=new AbortController();
    this.slots=Array.from(this.querySelectorAll('[data-fit-slot]'));
    if(!this.slots.length)return;
    this.status=this.querySelector('[data-fit-status]');
    this.save=this.querySelector('[data-fit-save]');
    this.querySelectorAll('[data-fit-next],[data-fit-actions]').forEach(e=>e.hidden=false);
    try {
      const saved=JSON.parse(localStorage.getItem(this.dataset.storageKey)||'{}');
      this.slots.forEach(slot=>{
        const options=this.options(slot);
        const found=options.findIndex(o=>o.dataset.productId===saved[slot.dataset.slotId]);
        this.select(slot,found<0?0:found);
      });
    }catch{/* The studio works normally when storage is unavailable. */}
    this.addEventListener('click',event=>{
      const button=event.target.closest('button');if(!button)return;
      if(button.matches('[data-fit-next]')){
        const slot=button.closest('[data-fit-slot]');const options=this.options(slot);
        const index=options.findIndex(o=>!o.hidden);
        this.select(slot,(index+1)%options.length);this.save.textContent=this.save.dataset.save;
        this.status.textContent=slot.querySelector('[data-fit-option]:not([hidden]) .sp-fit__name').textContent;
      }else if(button.matches('[data-fit-save]')){
        const saved=Object.fromEntries(this.slots.map(slot=>[slot.dataset.slotId,this.options(slot).find(o=>!o.hidden).dataset.productId]));
        try{localStorage.setItem(this.dataset.storageKey,JSON.stringify(saved));this.save.textContent=this.save.dataset.saved;this.status.textContent='Tu selección está guardada en este dispositivo.';}
        catch{this.status.textContent='Este navegador no permite guardar. Puedes seguir combinando prendas.';}
      }else if(button.matches('[data-fit-reset]')){
        this.slots.forEach(slot=>this.select(slot,0));try{localStorage.removeItem(this.dataset.storageKey);}catch{}
        this.save.textContent=this.save.dataset.save;this.status.textContent='Selección reiniciada.';
      }
    },{signal:this.controller.signal});
    document.addEventListener('shopify:block:select',event=>{
      if(this.contains(event.target)){const disclosure=this.closest('.sp-fit__disclosure');if(disclosure)disclosure.open=true;}
    },{signal:this.controller.signal});
  }
  options(slot){return Array.from(slot.querySelectorAll('[data-fit-option]'));}
  select(slot,index){this.options(slot).forEach((option,i)=>option.hidden=i!==index);if(this.isConnected&&slot.getBoundingClientRect().height>0)spAnimate(this.options(slot)[index],[{opacity:0,transform:'translateY(6px)'},{opacity:1,transform:'translateY(0)'}],220);}
  disconnectedCallback(){this.controller?.abort();this.controller=null;}
}
if(!customElements.get('sp-campaign'))customElements.define('sp-campaign',SPCampaign);
if(!customElements.get('sp-product-flip'))customElements.define('sp-product-flip',SPProductFlip);
if(!customElements.get('sp-fit-studio'))customElements.define('sp-fit-studio',SPFitStudio);

class SPDropClock extends HTMLElement {
 connectedCallback(){
  if(this.timer)clearInterval(this.timer);
  this.output=this.querySelector('[data-countdown]');this.time=this.querySelector('time');
  const raw=this.dataset.date||'';
  if(!this.output||!this.time)return;
  if(!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?(Z|[+-]\d{2}:\d{2})$/.test(raw)||!Number.isFinite(Date.parse(raw))){this.hidden=true;return;}
  this.end=Date.parse(raw);this.hidden=false;this.output.hidden=false;
  this.time.textContent=new Intl.DateTimeFormat('es',{timeZone:'America/Panama',day:'numeric',month:'long',year:'numeric'}).format(this.end);
  let hour=this.querySelector('[data-sp-clock-hour]');
  if(!hour){hour=document.createElement('p');hour.dataset.spClockHour='';this.time.after(hour);}
  hour.textContent=new Intl.DateTimeFormat('es',{timeZone:'America/Panama',hour:'numeric',minute:'2-digit',hour12:true}).format(this.end)+' · UTC−5';
  this.setAttribute('data-sp-clear-clock','');this.output.setAttribute('aria-live','off');this.output.setAttribute('aria-label','Tiempo restante para la fecha del drop');
  this.values=[];this.output.replaceChildren();
  for(const label of ['DÍAS','HORAS','MINUTOS','SEGUNDOS']){
   const unit=document.createElement('span');unit.className='sp-clock-unit';
   const value=document.createElement('strong');value.textContent='00';
   const name=document.createElement('span');name.textContent=label;unit.append(value,name);this.output.append(unit);this.values.push(value);
  }
  if(!this.querySelector('style[data-sp-clock-style]')){
   const style=document.createElement('style');style.dataset.spClockStyle='';style.textContent=`
   sp-drop-clock[data-sp-clear-clock]{display:block;min-width:0;width:min(100%,400px);line-height:1.4;font-variant-numeric:tabular-nums}
   sp-drop-clock[data-sp-clear-clock] time{display:block;font-size:17px;font-weight:650;letter-spacing:0}
   sp-drop-clock[data-sp-clear-clock] [data-sp-clock-hour]{font-size:12px;color:#555;margin:6px 0 18px}
   sp-drop-clock[data-sp-clear-clock] [data-countdown]{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:14px;font-size:12px;margin:0;width:100%}
   sp-drop-clock[data-sp-clear-clock] .sp-clock-unit{display:flex;flex-direction:column;gap:6px;text-align:left}
   sp-drop-clock[data-sp-clear-clock] .sp-clock-unit strong{font-size:clamp(28px,4vw,42px);line-height:1;font-weight:750;color:#111}
   sp-drop-clock[data-sp-clear-clock] .sp-clock-unit>span{font-size:9px;letter-spacing:.02em;color:#555}
   sp-drop-clock[data-sp-clear-clock][data-sp-ended] [data-countdown]{display:block;font-size:13px}
   .sp-drop--compact:has(sp-drop-clock[data-sp-clear-clock]){align-items:center}
   .sp-drop--compact:has(sp-drop-clock[data-sp-clear-clock]) .sp-drop__details{flex:1;justify-content:flex-end;gap:24px}
   @media(max-width:749px){.sp-drop--compact:has(sp-drop-clock[data-sp-clear-clock]) .sp-drop__details{width:100%;flex-basis:100%;justify-content:flex-start}sp-drop-clock[data-sp-clear-clock]{width:100%}sp-drop-clock[data-sp-clear-clock] [data-countdown]{gap:10px}}
   `;this.append(style);
  }
  if(this.tick())this.timer=setInterval(()=>this.tick(),1000);
 }
 tick(){
  const left=this.end-Date.now();
  if(left<=0){this.output.textContent=this.dataset.ended||'Ya llegó la fecha. Consulta la colección.';this.setAttribute('data-sp-ended','');clearInterval(this.timer);this.timer=null;return false;}
  this.removeAttribute('data-sp-ended');const total=Math.ceil(left/1000);
  const units=[Math.floor(total/86400),Math.floor(total/3600)%24,Math.floor(total/60)%60,total%60];
  units.forEach((value,index)=>{const text=String(value).padStart(2,'0');if(this.values[index].textContent!==text)this.values[index].textContent=text;});return true;
 }
 disconnectedCallback(){clearInterval(this.timer);this.timer=null;}
}

if(!customElements.get('sp-drop-clock'))customElements.define('sp-drop-clock',SPDropClock);

class SPCoffeeMenu extends HTMLElement {
  connectedCallback() {
    if(this.controller)return;
    this.controller=new AbortController();
    this.items=Array.from(this.querySelectorAll('[data-coffee-item]'));
    this.groups=Array.from(this.querySelectorAll('[data-coffee-group]'));
    this.search=this.querySelector('[data-coffee-search]');
    this.category='';
    this.dialog=this.querySelector('[data-coffee-dialog]');
    const opts={signal:this.controller.signal};
    this.querySelector('[data-coffee-tools]').hidden=false;
    if(typeof this.dialog?.showModal==='function')this.querySelectorAll('[data-coffee-open]').forEach(b=>b.hidden=false);
    this.search.addEventListener('input',()=>this.filter(),opts);
    this.addEventListener('click',event=>{
      const button=event.target.closest('button');if(!button)return;
      if(button.hasAttribute('data-coffee-category')){
        this.category=button.dataset.coffeeCategory;
        this.filter();
      }else if(button.hasAttribute('data-coffee-clear')){
        this.search.value='';this.category='';this.filter();this.search.focus();
      }else if(button.hasAttribute('data-coffee-open'))this.open(button);
      else if(button.hasAttribute('data-coffee-close'))this.dialog.close();
    },opts);
    this.dialog.addEventListener('click',event=>{
      if(event.target!==this.dialog)return;
      const rect=this.dialog.getBoundingClientRect();
      if(event.clientX<rect.left||event.clientX>rect.right||event.clientY<rect.top||event.clientY>rect.bottom)this.dialog.close();
    },opts);
    this.dialog.addEventListener('close',()=>this.trigger?.focus({preventScroll:true}),opts);
    document.addEventListener('shopify:block:select',event=>{
      if(!this.contains(event.target))return;
      this.search.value='';this.category='';this.filter();
    },opts);
    this.filter();
  }
  normalize(value){return value.normalize('NFD').replace(/[\u0300-\u036f]/g,'').toLowerCase().trim();}
  filter(){
    const query=this.normalize(this.search.value);
    let count=0;
    this.items.forEach(item=>{
      const match=(!this.category||item.dataset.category===this.category)&&this.normalize(item.textContent).includes(query);
      item.hidden=!match;if(match)count++;
    });
    this.groups.forEach(group=>group.hidden=!group.querySelector('[data-coffee-item]:not([hidden])'));
    this.querySelectorAll('[data-coffee-category]').forEach(button=>button.setAttribute('aria-pressed',String(button.dataset.coffeeCategory===this.category)));
    this.querySelector('[data-coffee-results]').textContent=count+' '+(count===1?'opción':'opciones');
    this.querySelector('[data-coffee-empty]').hidden=count>0;
  }
  open(button){
    const item=button.closest('[data-coffee-item]');this.trigger=button;
    const text=(target,source)=>{this.querySelector(target).textContent=item.querySelector(source)?.textContent||'';};
    text('[data-coffee-detail-name]','[data-coffee-name]');
    text('[data-coffee-detail-description]','[data-coffee-description]');
    text('[data-coffee-detail-price]','[data-coffee-price]');
    this.querySelector('[data-coffee-detail-category]').textContent=item.dataset.category;
    this.querySelector('[data-coffee-detail-availability]').textContent=item.hasAttribute('data-coffee-unavailable')?'NO DISPONIBLE':'';
    const photo=this.querySelector('[data-coffee-detail-photo]');const image=item.querySelector('img');
    photo.replaceChildren();photo.hidden=!image;
    if(image){const detail=image.cloneNode(true);detail.loading='eager';detail.sizes='(min-width:750px) 400px, 90vw';photo.append(detail);}
    this.dialog.showModal();
  }
  disconnectedCallback(){if(this.dialog?.open)this.dialog.close();this.controller?.abort();this.controller=null;}
}
if(!customElements.get('sp-coffee-menu'))customElements.define('sp-coffee-menu',SPCoffeeMenu);

class SPDiscovery extends HTMLElement {
  connectedCallback(){
    if(this.controller)return;
    this.controller=new AbortController();
    this.tabs=Array.from(this.querySelectorAll('[data-discovery-tab]'));
    this.panels=Array.from(this.querySelectorAll('[data-discovery-panel]'));
    if(!this.tabs.length)return;
    const opts={signal:this.controller.signal};
    this.classList.add('is-enhanced');
    this.querySelector('[data-discovery-tabs]').hidden=false;
    this.querySelector('[data-discovery-fallback]').hidden=true;
    let initial=0;
    if(this.hasAttribute('data-remember')){
      try{const remembered=sessionStorage.getItem(this.dataset.storageKey);const index=this.tabs.findIndex(t=>t.dataset.blockId===remembered);if(index>=0)initial=index;}catch{}
    }
    const anchor=this.panels.findIndex(p=>'#'+p.id===location.hash);if(anchor>=0)initial=anchor;
    this.select(initial);
    this.addEventListener('click',event=>{
      const search=event.target.closest('[data-search-launch]');
      if(search){const modal=document.getElementById('search-modal');if(typeof modal?.showDialog==='function'){event.preventDefault();modal.showDialog();}return;}
      const tab=event.target.closest('[data-discovery-tab]');
      if(tab)this.select(this.tabs.indexOf(tab),{animate:true,remember:true});
    },opts);
    this.querySelector('[data-discovery-tabs]').addEventListener('keydown',event=>{
      const tab=event.target.closest('[data-discovery-tab]');if(!tab)return;
      let index=this.tabs.indexOf(tab);
      if(event.key==='ArrowRight')index=(index+1)%this.tabs.length;
      else if(event.key==='ArrowLeft')index=(index+this.tabs.length-1)%this.tabs.length;
      else if(event.key==='Home')index=0;
      else if(event.key==='End')index=this.tabs.length-1;
      else return;
      event.preventDefault();this.select(index,{animate:true,remember:true,focus:true});
    },opts);
    document.addEventListener('shopify:block:select',event=>{
      if(!this.contains(event.target))return;
      const panel=event.target.closest('[data-discovery-panel]');if(panel)this.select(this.panels.indexOf(panel));
    },opts);
  }
  select(index,{animate=false,remember=false,focus=false}={}){
    if(index<0||index>=this.tabs.length)return;
    this.tabs.forEach((tab,i)=>{tab.setAttribute('aria-selected',String(i===index));tab.tabIndex=i===index?0:-1;});
    this.panels.forEach((panel,i)=>panel.hidden=i!==index);
    const tab=this.tabs[index];
    if(remember&&this.hasAttribute('data-remember')){try{sessionStorage.setItem(this.dataset.storageKey,tab.dataset.blockId);}catch{}}
    if(focus){tab.focus({preventScroll:true});tab.scrollIntoView({block:'nearest',inline:'nearest',behavior:'auto'});}
    if(animate)spAnimate(this.panels[index],[{opacity:0,transform:'translateY(6px)'},{opacity:1,transform:'translateY(0)'}],200);
    this.querySelector('[data-discovery-status]').textContent=tab.textContent.trim();
  }
  disconnectedCallback(){this.controller?.abort();this.controller=null;}
}
if(!customElements.get('sp-discovery'))customElements.define('sp-discovery',SPDiscovery);

// Motion confirms an action; content remains visible even if the controller fails.
const spSeen=new WeakSet();
const spRevealObserver='IntersectionObserver' in window?new IntersectionObserver(entries=>{
  entries.forEach(entry=>{if(!entry.isIntersecting)return;spRevealObserver.unobserve(entry.target);spAnimate(entry.target,[{opacity:.35,transform:'translateY(8px)'},{opacity:1,transform:'translateY(0)'}],320);});
},{threshold:.15}):null;
function spRegisterMotion(){
  document.querySelectorAll('.sp-film__caption,.sp-discovery .sp-section-head,.sp-fit__summary,.sp-drop--compact').forEach(element=>{
    if(spSeen.has(element))return;spSeen.add(element);spRevealObserver?.observe(element);
  });
}
if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',spRegisterMotion,{once:true});else spRegisterMotion();
document.addEventListener('shopify:section:load',spRegisterMotion);
document.addEventListener('toggle',event=>{
  const details=event.target;
  if(details instanceof HTMLDetailsElement&&details.matches('.sp-fit__disclosure')&&details.open)spAnimate(details.querySelector('.sp-fit__body'),[{opacity:0,transform:'translateY(8px)'},{opacity:1,transform:'translateY(0)'}],260);
},true);
